import {
  Alert,
  Badge,
  Button,
  DescriptionList,
  FormField,
  Inline,
  InlineItem,
  Input,
  Select,
  SettingsSection,
  SkeletonText,
  SortableList,
  Stack,
  Text,
  useToast,
  type DescriptionListItem,
} from '@hkdevs/design-system';
import { useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import {
  useLineItemTable,
  useSaveLineItemTable,
  type LineItemTable,
} from '../../api/queries/settings';
import { withReference } from '../../api/withReference';
import type { LineItemColumn } from '../lineItems/LineItemList';

type Format = LineItemColumn['format'];

/** A column while editing; `id` keeps a row's identity while its key is typed. */
export interface DraftColumn {
  id: string;
  key: string;
  label: string;
  format: Format;
  fixed: boolean;
}

export type ColumnErrors = Partial<Record<'key' | 'label' | 'format' | 'row', string>>;

export const MAX_CONFIGURED = 3;
export const LABEL_MAX = 30;
const AMOUNT = 'amount';

/** The formats a configured column can have, as Products admin names them. */
export const COLUMN_FORMATS: { value: Format; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'money', label: 'Money' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'percent', label: 'Percentage' },
];
const formatLabel = (format: Format) =>
  COLUMN_FORMATS.find((option) => option.value === format)?.label ?? format;

/** What each fixed column is. */
const FIXED_NAMES: Record<string, string> = {
  customer: 'Customer',
  productType: 'Product Type',
  amount: 'Amount Earned',
};

/** Upload columns a configured column can't show: the API refuses them too (ADR 0030). */
const RESERVED = new Set([
  'PAY DATE',
  'PRODUCT TYPE',
  'PAY CATEGORY',
  'TOTAL',
  'AMOUNT',
  'CUSTOMER NAME',
  'NOTE',
  'PAYEE EMAIL',
  'PAYEE NAME',
]);

/** An upload column as the API keys it: trimmed, spaces collapsed, upper-cased. */
export const normalizeColumn = (name: string) => name.trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * What the form checks before saving, as the API does: labels filled in, short and
 * unique (fixed columns included); each configured column a new, allowed upload column.
 */
export function validateColumns(columns: readonly DraftColumn[]): Record<string, ColumnErrors> {
  const errors: Record<string, ColumnErrors> = {};
  const labels = new Set<string>();
  const keys = new Set<string>();
  for (const column of columns) {
    const error: ColumnErrors = {};
    const label = column.label.trim();
    if (!label) error.label = 'Enter a label.';
    else if (label.length > LABEL_MAX) error.label = `Use at most ${LABEL_MAX} characters.`;
    else if (labels.has(label.toLowerCase())) error.label = `Another column is called ${label}.`;
    labels.add(label.toLowerCase());
    if (!column.fixed) {
      const key = normalizeColumn(column.key);
      if (!key) error.key = 'Enter the upload column.';
      else if (RESERVED.has(key)) {
        error.key = `${key} can’t be a column here: it has its own place or isn’t kept per line item.`;
      } else if (keys.has(key)) error.key = `${key} is already in the table.`;
      keys.add(key);
    }
    if (Object.keys(error).length > 0) errors[column.id] = error;
  }
  return errors;
}

let nextId = 0;
const draftOf = (table: LineItemTable): DraftColumn[] =>
  table.columns.map((column) => ({ ...column, id: column.fixed ? column.key : `c${nextId++}` }));

/** The draft as the API takes it: fixed columns by key, configured ones with a format. */
export const bodyOf = (columns: readonly DraftColumn[]) => ({
  columns: columns.map((column) =>
    column.fixed
      ? { key: column.key, label: column.label.trim() }
      : { key: normalizeColumn(column.key), label: column.label.trim(), format: column.format },
  ),
});

/** Moves the column at `from` to `to`. */
export function moveColumn(columns: readonly DraftColumn[], from: number, to: number) {
  const next = [...columns];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  return next;
}

/** The API's field errors (body.columns.<index>.<key>), by draft column. */
function serverErrors(error: unknown, columns: readonly DraftColumn[]) {
  const errors: Record<string, ColumnErrors> = {};
  if (!(error instanceof ApiError)) return errors;
  for (const field of error.problem.errors ?? []) {
    const [, , index, key] = field.loc;
    const column = typeof index === 'number' ? columns[index] : undefined;
    if (!column) continue;
    const slot = key === 'key' || key === 'label' || key === 'format' ? key : 'row';
    errors[column.id] = { ...errors[column.id], [slot]: field.message };
  }
  return errors;
}

function ColumnRow({
  column,
  errors,
  disabled,
  onChange,
  onRemove,
}: {
  column: DraftColumn;
  errors: ColumnErrors | undefined;
  disabled: boolean;
  onChange: (column: DraftColumn) => void;
  onRemove?: () => void;
}) {
  return (
    <Stack as="span" gap={2}>
      <Inline as="span" gap={3} rowGap={2} align="start">
        <InlineItem as="span" grow>
          <FormField label="Label" error={errors?.label}>
            <Input
              value={column.label}
              maxLength={LABEL_MAX + 10}
              disabled={disabled}
              onChange={(event) => onChange({ ...column, label: event.target.value })}
            />
          </FormField>
        </InlineItem>
        {column.fixed ? (
          <Text as="span" size="meta" tone="muted">
            {FIXED_NAMES[column.key]}
            {column.key === AMOUNT ? ': always shown, always last' : ': always shown'}
          </Text>
        ) : (
          <>
            <InlineItem as="span" grow>
              <FormField
                label="Upload column"
                error={errors?.key}
                hint="As the sheet’s header names it."
              >
                <Input
                  value={column.key}
                  disabled={disabled}
                  onChange={(event) => onChange({ ...column, key: event.target.value })}
                />
              </FormField>
            </InlineItem>
            <FormField label="Format" error={errors?.format}>
              <Select
                value={column.format}
                options={COLUMN_FORMATS}
                disabled={disabled}
                onChange={(event) => onChange({ ...column, format: event.target.value as Format })}
              />
            </FormField>
          </>
        )}
      </Inline>
      {errors?.row && (
        <Text as="span" size="meta" tone="danger">
          {errors.row}
        </Text>
      )}
      {onRemove && (
        <Inline as="span" justify="end">
          <Button
            size="sm"
            variant="ghost"
            leadingIcon="trash"
            disabled={disabled}
            onClick={onRemove}
          >
            Remove {column.label.trim() || 'column'}
          </Button>
        </Inline>
      )}
    </Stack>
  );
}

/**
 * The line item table (ADR 0030): its columns in order, each with a label. Customer,
 * Product Type and Amount Earned can be moved (Amount Earned stays last) and relabelled,
 * never removed; up to 3 more columns show an upload column's values.
 */
export function LineItemTableSection() {
  const table = useLineItemTable();
  const save = useSaveLineItemTable();
  const { toast } = useToast();
  const [draft, setDraft] = useState<DraftColumn[]>([]);
  const [errors, setErrors] = useState<Record<string, ColumnErrors>>({});
  const addRef = useRef<HTMLButtonElement>(null);

  if (table.isError) {
    return (
      <Alert
        tone="danger"
        title="The line item table couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void table.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }
  if (!table.data) return <SkeletonText lines={4} />;

  const saved = table.data;
  const amount = draft.find((column) => column.key === AMOUNT);
  const movable = draft.filter((column) => column.key !== AMOUNT);
  const configured = draft.filter((column) => !column.fixed).length;
  const dirty = JSON.stringify(bodyOf(draft)) !== JSON.stringify(bodyOf(draftOf(saved)));
  const saving = save.isPending;

  const update = (next: DraftColumn) =>
    setDraft(draft.map((column) => (column.id === next.id ? next : column)));
  const ordered = (columns: DraftColumn[]) => (amount ? [...columns, amount] : columns);

  const items: DescriptionListItem[] = saved.columns.map((column) => ({
    key: column.key,
    term: column.label,
    description: column.fixed ? (
      <Text tone="muted">{FIXED_NAMES[column.key]}</Text>
    ) : (
      <Inline gap={2}>
        <Text>{column.key}</Text>
        <Badge size="sm" tone="neutral">
          {formatLabel(column.format)}
        </Badge>
      </Inline>
    ),
  }));

  return (
    <SettingsSection
      title="Line item table"
      description="The columns of every line item table (a check, pending pay, a rep’s view), in order. The labels of Customer, Product Type and Amount Earned are used wherever the app names them: the tables, statements and Line-item Search."
      mode="form"
      editLabel="Edit columns"
      onEditingChange={(editing) => {
        if (editing) {
          setDraft(draftOf(saved));
          setErrors({});
        }
      }}
      dirty={dirty}
      onSave={() => {
        const found = validateColumns(draft);
        setErrors(found);
        if (Object.keys(found).length > 0) {
          return Promise.reject(new Error('Some columns need fixing before they’re saved.'));
        }
        return save.mutateAsync(bodyOf(draft)).then(
          () => {
            toast({ tone: 'success', title: 'Line item table saved' });
          },
          (error: unknown) => {
            const fromServer = serverErrors(error, draft);
            setErrors(fromServer);
            if (Object.keys(fromServer).length === 0) {
              toast(withReference({ tone: 'danger', title: 'The table couldn’t be saved' }, error));
            }
            throw new Error(
              error instanceof ApiError
                ? (error.problem.detail ?? error.problem.title)
                : 'Check your connection and try again.',
            );
          },
        );
      }}
      form={
        <Stack gap={4}>
          <Text size="meta" tone="muted">
            Uploads check the values in Date, Money, Number, Decimal and Percentage columns from now
            on. Line items already uploaded aren’t checked again: a value that doesn’t fit its
            format shows as it was uploaded.
          </Text>
          <SortableList
            label="Line item table columns, in order"
            items={movable}
            getKey={(column) => column.id}
            getLabel={(column) => column.label.trim() || 'New column'}
            disabled={saving}
            onReorder={(from, to) => setDraft(ordered(moveColumn(movable, from, to)))}
            renderItem={(column) => (
              <ColumnRow
                column={column}
                errors={errors[column.id]}
                disabled={saving}
                onChange={update}
                {...(!column.fixed && {
                  onRemove: () => {
                    setDraft(draft.filter((other) => other.id !== column.id));
                    requestAnimationFrame(() => addRef.current?.focus());
                  },
                })}
              />
            )}
          />
          {amount && (
            <ColumnRow
              column={amount}
              errors={errors[amount.id]}
              disabled={saving}
              onChange={update}
            />
          )}
          <Inline gap={3} align="center">
            <Button
              ref={addRef}
              size="sm"
              variant="secondary"
              leadingIcon="plus"
              disabled={saving || configured >= MAX_CONFIGURED}
              onClick={() =>
                setDraft(
                  ordered([
                    ...movable,
                    { id: `c${nextId++}`, key: '', label: '', format: 'text', fixed: false },
                  ]),
                )
              }
            >
              Add column
            </Button>
            <Text size="meta" tone="muted">
              {configured >= MAX_CONFIGURED
                ? `The table has the most columns it can: ${MAX_CONFIGURED} besides the fixed ones.`
                : `Up to ${MAX_CONFIGURED} besides Customer, Product Type and Amount Earned.`}
            </Text>
          </Inline>
        </Stack>
      }
    >
      <DescriptionList items={items} />
    </SettingsSection>
  );
}
